import { useTranslation } from 'react-i18next';
import { GovMessage } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useAuthorizedLabel } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useEPeticeDetailContext } from '@gov-nx/module/page';
import { Button, ContentLayout, Icon, LayoutGap } from '@gov-nx/ui/web';
import { SouhlasCheckbox } from '../../../Autorizace/SouhlasCheckbox';

const SUBMIT_FORM_ID = 'EPETICE_SUBMIT_FORM';

interface EPetitionClosedStatusBannerProps {
	code: PageCode;
}

export const EPetitionClosedStatusBanner = ({
	code,
}: EPetitionClosedStatusBannerProps) => {
	const { t } = useTranslation(code);
	const authLabel = useAuthorizedLabel();
	const {
		petition,
		controls,
		ePetitionSubmitClose,
		handleEPetitionDownload,
		submitFormDefinition,
		handleSubmitPetitionSubmit,
	} = useEPeticeDetailContext();

	const sendLabel = authLabel(
		t('akce.poslat'),
		ePetitionSubmitClose.isAuthorizationEnforcedPodat
	);

	return (
		<ContentLayout>
			<LayoutGap gap="l">
				<h2 className="text-headline-s!">{t('pole.zpusob.predani-vyberte')}</h2>

				<GovMessage
					color="primary"
					type="subtle"
					icon={
						ePetitionSubmitClose.isBeforeSubmit ? (
							<Icon
								type="components"
								name="lightbulb-fill"
							/>
						) : (
							<Icon
								type="components"
								name="exclamation-triangle-fill"
							/>
						)
					}>
					<div className="flex-col flex justify-between items-start gap-(--templates-margin-m) [ md:flex-row ]">
						<div className="flex-col flex gap-(--templates-margin-s) flex-1">
							{ePetitionSubmitClose.isBeforeSubmit
								? t('zprava.podani.zbytek-dni', {
										namespace: code,
										days: ePetitionSubmitClose.daysLeft,
									})
								: t('zprava.podani.lhuta-ubehla')}
						</div>
					</div>
				</GovMessage>

				<div className="flex flex-col gap-(--templates-margin-m) pt-(--spacing-m-nudge) pb-(--spacing-m-nudge) [ md:flex-row ]">
					<Icon
						name="datovka-fill"
						type="templates"
						size="5xl"
						className="text-secondary-600"
					/>
					<div className="flex flex-col gap-(--spacing-xs)">
						<h3 className="text-headline-xs! text-(--text-primary-color)">
							{t('pole.zpusob-podani-datovka')}
						</h3>
						<p className="text-(--text-secondary)">
							{t('pole.podani-adresat')}:{' '}
							<strong>{petition?.adresat?.nazev}</strong> (
							{petition?.adresat?.datovaSchrankaId})
						</p>
						<p className="text-(--text-secondary)">{t('pole.datovka-popis')}</p>
					</div>
					<GovFormWeb
						id={SUBMIT_FORM_ID}
						formDefinition={submitFormDefinition}
						onSubmit={handleSubmitPetitionSubmit}
						className="md:ml-auto!">
						<SouhlasCheckbox
							show={ePetitionSubmitClose.isLowAuthorizationPodat}
							disabled={
								controls.isSubmitProcessing ||
								!ePetitionSubmitClose.isBeforeSubmit
							}
						/>
						<Button
							form={SUBMIT_FORM_ID}
							nativeType="submit"
							isDisabled={
								controls.isSubmitProcessing ||
								!ePetitionSubmitClose.isBeforeSubmit
							}
							isLoading={controls.isSubmitProcessing}
							color="primary"
							type="solid"
							size="m"
							aria-label={sendLabel}
							className="md:ml-auto!"
							iconStart={
								<Icon
									name="send"
									type="templates"
								/>
							}>
							{sendLabel}
						</Button>
					</GovFormWeb>
				</div>
				<div className="flex flex-col gap-(--templates-margin-m) pt-(--spacing-m-nudge) pb-(--spacing-m-nudge) [ md:flex-row ]">
					<Icon
						name="journal-text"
						type="templates"
						size="5xl"
						className="text-(--text-primary-color)"
					/>
					<div className="flex flex-col gap-(--spacing-xs)">
						<h3 className="text-headline-xs! text-(--text-primary-color)">
							{t('pole.zpusob-podani-osobne')}
						</h3>
						<p className="text-(--text-secondary)">{t('pole.osobne-popis')}</p>
					</div>
					<Button
						onClick={() => handleEPetitionDownload()}
						isLoading={controls.isDownloadProcessing}
						isDisabled={
							controls.isDownloadProcessing ||
							!ePetitionSubmitClose.isBeforeSubmit
						}
						color="primary"
						nativeType="button"
						type="solid"
						size="m"
						aria-label={t('akce.stahnout')}
						className="md:ml-auto!"
						iconStart={
							<Icon
								name="download"
								type="templates"
							/>
						}>
						{t('akce.stahnout')}
					</Button>
				</div>
			</LayoutGap>
		</ContentLayout>
	);
};
